import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import { translateKey } from 'src/locales/translate';


const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root': {
    paddingBottom: theme.spacing(1),
  },
  '& .MuiDialogContent-root': {
    paddingTop: theme.spacing(2),
  },
}));

interface SpecializationRemovalWarningModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  affectedServices: Array<{
    serviceId?: string;
    serviceName: string;
    specializationName: string;
  }>;
  isProfessional?: boolean;
  affectedProfessionals?: Array<{
    professionalName: string;
    affectedServices: Array<{
      serviceName: string;
      specializationName: string;
    }>;
  }>;
  title?: string;
  message?: string;
}

export default function SpecializationRemovalWarningModal({
  open,
  onClose,
  onConfirm,
  affectedServices,
  isProfessional = true,
  affectedProfessionals,
  title,
  message,
}: SpecializationRemovalWarningModalProps) {
  const totalAffectedServices = isProfessional
    ? affectedServices.length
    : affectedProfessionals?.reduce(
        (sum, prof) => sum + prof.affectedServices.length,
        0,
      ) || 0;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title ||
          (isProfessional
            ? translateKey("hardcoded.usuniecie_specjalizacji")
            : translateKey("hardcoded.usuniecie_specjalizacji_z_placowki"))}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {message ||
            (isProfessional ? (
              <>{translateKey("hardcoded.wybrane_specjalizacje_maja_przypisane_uslugi_w_ustawieniach_wizyt")}{' '}
                <strong>{totalAffectedServices}</strong>{' '}
                {totalAffectedServices === 1 ? translateKey("hardcoded.uslugi") : translateKey("hardcoded.uslug")}.
              </>
            ) : (
              <>{translateKey("hardcoded.wybrane_specjalizacje_maja_przypisane_uslugi_u_profesjonalistow_w")}{' '}
                <strong>{totalAffectedServices}</strong>{' '}
                {totalAffectedServices === 1 ? translateKey("hardcoded.uslugi") : translateKey("hardcoded.uslug")} u{' '}
                {affectedProfessionals?.length || 0}{' '}
                {(affectedProfessionals?.length || 0) === 1
                  ? 'profesjonalisty'
                  : translateKey("hardcoded.profesjonalistow")}
                .
              </>
            ))}
        </Alert>

        {isProfessional ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translateKey("hardcoded.nastepujace_uslugi_zostana_usuniete")}</Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                bgcolor: 'background.neutral',
                borderRadius: 1,
                p: 1,
              }}
            >
              <List dense disablePadding>
                {affectedServices.map((service, index) => (
                  <ListItem key={service.serviceId || index} disableGutters>
                    <ListItemText
                      primary={service.serviceName}
                      secondary={mapSpecializationNameIdToString(
                        service.specializationName,
                      )}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translateKey("hardcoded.wplyw_na_profesjonalistow")}</Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                bgcolor: 'background.neutral',
                borderRadius: 1,
                p: 1,
              }}
            >
              <List dense disablePadding>
                {affectedProfessionals?.map((prof, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemText
                      primary={prof.professionalName}
                      secondary={`${prof.affectedServices.length} ${
                        prof.affectedServices.length === 1 ? translateKey("hardcoded.usluga") : translateKey("hardcoded.uslug")
                      }: ${prof.affectedServices
                        .map((s) => s.serviceName)
                        .join(', ')}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                      }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{translateKey("hardcoded.uslugi_beda_mozna_ponownie_wlaczyc_po_przywroceniu_specjalizacji")}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{translateKey("common.cancel")}</Button>
        <Button onClick={onConfirm} variant="contained" color="error">{translateKey("hardcoded.potwierdz_usuniecie")}</Button>
      </DialogActions>
    </StyledDialog>
  );
}
